import { useQuery, type UseQueryOptions } from "@tanstack/react-query";
import { tournamentsApi } from "../../api/tournaments";
import type { TournamentDetail, Tournament, PagedResponse, Player, PlayerAwardsContext } from "../../types/domain";
import { normalizeId, type MaybeId } from "./queryKeyUtils";

export interface TournamentsPagedParams {
  page: number;
  ladder: string | null;
}

export const tournamentKeys = {
  all: ["tournaments"] as const,
  list: () => ["tournaments", "list"] as const,
  listPaged: (params: TournamentsPagedParams) => ["tournaments", "listPaged", params] as const,
  detail: (id: MaybeId) => ["tournaments", "detail", normalizeId(id)] as const,
  blastQueue: (tournamentId: MaybeId) => ["tournaments", "blastQueue", normalizeId(tournamentId)] as const,
  awardsContext: (tournamentId: MaybeId) => ["tournaments", "awardsContext", normalizeId(tournamentId)] as const,
};

type TournamentQueryOptions = Omit<
  UseQueryOptions<TournamentDetail, Error, TournamentDetail, ReturnType<typeof tournamentKeys.detail>>,
  "queryKey" | "queryFn"
>;

export const useTournament = (id: MaybeId, options: Partial<TournamentQueryOptions> = {}) =>
  useQuery({
    queryKey: tournamentKeys.detail(id),
    queryFn: () => tournamentsApi.getById(id!),
    enabled: id != null,
    ...options,
  });

export const useTournaments = () =>
  useQuery({
    queryKey: tournamentKeys.list(),
    queryFn: tournamentsApi.list,
  });

export const useTournamentsPaged = ({ page, ladder }: TournamentsPagedParams) =>
  useQuery({
    queryKey: tournamentKeys.listPaged({ page, ladder }),
    queryFn: () => tournamentsApi.listPaged({ page, ladder }),
    placeholderData: (prev: PagedResponse<Tournament> | undefined) => prev,
  });

export const useBlastQueue = (tournamentId: MaybeId) =>
  useQuery<Player[]>({
    queryKey: tournamentKeys.blastQueue(tournamentId),
    queryFn: () => tournamentsApi.getBlastQueue(Number(tournamentId)),
    enabled: tournamentId != null,
  });

export const useAwardsContext = (tournamentId: MaybeId, enabled: boolean = true) =>
  useQuery<PlayerAwardsContext[]>({
    queryKey: tournamentKeys.awardsContext(tournamentId),
    queryFn: () => tournamentsApi.getAwardsContext(Number(tournamentId)),
    enabled: enabled && tournamentId != null,
    staleTime: Infinity,
  });
